import type { ReactNode } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";

// ---- PIEZAS COMPARTIDAS DEL PORTAL FIANZAS ----

export type ChipTone =
  | "urgent"
  | "size"
  | "ok"
  | "risk"
  | "info"
  | "warm"
  | "competition";

const CHIP_TONES: Record<ChipTone, string> = {
  urgent: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  size: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  ok: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  risk: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  info: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  warm: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  competition:
    "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
};

/** Etiqueta corta que dice por qué una fila importa. */
export function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold",
        CHIP_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

/** Selector de los últimos 6 meses, para el período de la cabecera. */
export function MonthSelect({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (month: string) => void;
  className?: string;
}) {
  return (
    <select
      aria-label="Mes"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        "cursor-pointer rounded-lg bg-transparent font-extrabold capitalize outline-none",
        className,
      )}
    >
      {Array.from({ length: 6 }, (_, index) =>
        dayjs().startOf("month").subtract(index, "month"),
      ).map((month) => (
        <option key={month.format("YYYY-MM")} value={month.format("YYYY-MM")}>
          {month.format("MMMM YYYY")}
        </option>
      ))}
    </select>
  );
}

/** Cabecera de página: organización arriba, título con su período y bajada. */
export function PageHeader({
  eyebrow,
  title,
  accent,
  subtitle,
  actions,
}: {
  eyebrow?: string | null;
  title: string;
  accent?: ReactNode;
  subtitle: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
          {eyebrow ?? "Licitigo Fianzas"}
        </p>
        <h1 className="flex flex-wrap items-center gap-x-2 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50">
          {title}
          {accent && (
            <>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              {accent}
            </>
          )}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Tarjeta blanca de sección, con título, bajada y una acción a la derecha. */
export function SectionCard({
  title,
  subtitle,
  action,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-4 rounded-2xl border bg-card p-5 shadow-sm",
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-50">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Indicador del período, con su comparación y una bajada. Con `to` es un enlace. */
export function KpiCard({
  label,
  value,
  delta,
  hint,
  valueClassName,
  to,
}: {
  label: string;
  value: ReactNode;
  delta?: { text: string; positive: boolean } | null;
  hint?: ReactNode;
  valueClassName?: string;
  to?: string;
}) {
  const body = (
    <>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            "text-2xl font-extrabold text-slate-900 dark:text-slate-50",
            valueClassName,
          )}
        >
          {value}
        </span>
        {delta && (
          <span
            className={cn(
              "text-xs font-bold",
              delta.positive ? "text-emerald-700" : "text-red-600",
            )}
          >
            {delta.text}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </>
  );
  const className =
    "flex flex-col gap-1 rounded-2xl border bg-card p-4 shadow-sm";
  return to ? (
    <Link to={to} className={cn(className, "cursor-pointer transition-colors hover:border-primary/40")}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

/** Fila de barra horizontal. Con `onClick` funciona como filtro. */
export function BarRow({
  label,
  value,
  pct,
  barClassName,
  onClick,
  active,
  dimmed,
}: {
  label: string;
  value: string;
  pct: number;
  barClassName: string;
  onClick?: () => void;
  active?: boolean;
  dimmed?: boolean;
}) {
  const content = (
    <>
      <span className="truncate text-left text-sm font-semibold text-slate-700 dark:text-slate-200">
        {label}
      </span>
      <span className="block h-2.5 rounded-full bg-slate-100 dark:bg-slate-800">
        <span
          className={cn(
            "block h-2.5 rounded-full",
            dimmed ? "bg-slate-300 dark:bg-slate-600" : barClassName,
          )}
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </span>
      <span className="text-right text-sm font-bold text-slate-900 dark:text-slate-50">
        {value}
      </span>
    </>
  );
  const className =
    "grid w-full grid-cols-[minmax(0,9rem)_minmax(0,1fr)_5.5rem] items-center gap-3 rounded-lg px-2 py-1.5";
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        className,
        "cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60",
        active && "bg-amber-50 ring-1 ring-amber-300 dark:bg-amber-950/30",
      )}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

/** Mensaje para una lista vacía. */
export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed p-4 text-center text-sm text-slate-500">
      {children}
    </p>
  );
}
